/**
 * @fileoverview Quick Add Product Modal
 * @description Implements a modal dialog for quick product viewing and adding to cart
 *
 * Features:
 * - Dynamic product loading
 * - Image gallery with Swiper
 * - Variant selection
 * - Payment button integration
 * - 3D model support
 * - Responsive image sizing
 *
 * HTML Structure Required:
 * - Element with id starting with "QuickAddInfo-" for modal content
 * - Elements with class "js-media-list" for product gallery
 * - Elements with class "swiper-btn--next" and "swiper-btn--prev" for navigation
 * - Elements with class "loading-overlay__spinner" for loading indicator
 */

if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      /**
       * @constructor
       * @description Initializes the quick add modal
       * - Sets up modal content reference
       */
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
      }

      /**
       * @function hide
       * @description Hides the modal and cleans up
       * @param {boolean} preventFocus - Whether to prevent focus on the opener
       */
      hide(preventFocus = false) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';
        window.pauseAllMedia();
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      /**
       * @function show
       * @description Shows the modal and loads product data
       * @param {HTMLElement} opener - The element that opened the modal
       */
      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        if (opener.querySelector('.loading-overlay__spinner')) {
          opener
            .querySelector('.loading-overlay__spinner')
            .classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );
            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );
            this.preventDuplicatedIDs();
            this.removeDOMElements();
            this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();
            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (opener.querySelector('.loading-overlay__spinner')) {
              opener
                .querySelector('.loading-overlay__spinner')
                .classList.add('hidden');
            }

            var slider = new Swiper('.js-media-list', {
              slidesPerView: 1,
              navigation: {
                nextEl: '.swiper-btn--next',
                prevEl: '.swiper-btn--prev',
              },
            });
          });
      }

      /**
       * @function setInnerHTML
       * @description Sets inner HTML and reinitializes scripts
       * @param {HTMLElement} element - The element to set HTML for
       * @param {string} html - The HTML content to set
       */
      setInnerHTML(element, html) {
        element.innerHTML = html;

        // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
        element.querySelectorAll('script').forEach((oldScriptTag) => {
          const newScriptTag = document.createElement('script');
          Array.from(oldScriptTag.attributes).forEach((attribute) => {
            newScriptTag.setAttribute(attribute.name, attribute.value);
          });
          newScriptTag.appendChild(
            document.createTextNode(oldScriptTag.innerHTML)
          );
          oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
      }

      /**
       * @function removeDOMElements
       * @description Removes unnecessary DOM elements from the modal
       */
      removeDOMElements() {
        const pickupAvailability = this.productElement.querySelector(
          'pickup-availability'
        );
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = this.productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const gift = this.productElement.querySelector('.customer');
        if (gift) gift.remove();
      }

      /**
       * @function preventDuplicatedIDs
       * @description Prevents ID conflicts by modifying section IDs
       */
      preventDuplicatedIDs() {
        const sectionId = this.productElement.dataset.section;
        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );
        this.productElement
          .querySelectorAll('variant-selects, variant-radios')
          .forEach((variantSelect) => {
            variantSelect.dataset.originalSection = sectionId;
          });
      }

      /**
       * @function preventVariantURLSwitching
       * @description Prevents URL updates when changing variants
       */
      preventVariantURLSwitching() {
        if (this.modalContent.querySelector('variant-radios,variant-selects')) {
          this.modalContent
            .querySelector('variant-radios,variant-selects')
            .setAttribute('data-update-url', 'false');
        }
      }

      /**
       * @function removeGalleryListSemantic
       * @description Removes semantic roles from gallery list elements
       */
      removeGalleryListSemantic() {
        const galleryList = this.modalContent.querySelector(
          '[id^="Slider-Gallery"]'
        );
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => li.setAttribute('role', 'presentation'));
      }

      /**
       * @function updateImageSizes
       * @description Updates image sizes based on product layout
       */
      updateImageSizes() {
        const product = this.modalContent.querySelector('.product');
        const desktopColumns = product.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) =>
          img.setAttribute('sizes', mediaImageSizes)
        );
      }
    }
  );
}
